/**
 * ProductBomConfigTable.tsx
 *
 * "Bảng Cấu Hình Thông Tin BOM (Ma Trận Màu × Gỗ × Kết Cấu)"
 * Replaces the old variant/mapping table on the Product Detail page.
 */
import { useEffect, useRef, useState } from 'react'
import { FolderOpen, Plus, Trash2, ChevronDown } from 'lucide-react'
import type { BomConfigRow } from '../types/bomSpreadsheetTypes'
import { createBlankBomConfigRow } from '../types/bomSpreadsheetTypes'

interface ProductBomConfigTableProps {
  productCode: string
  configs: BomConfigRow[]
  availableColors?: string[]
  availableWoods?: string[]
  onChange: (configs: BomConfigRow[]) => void
  onOpenBom: (config: BomConfigRow) => void
}

const DEFAULT_ASSEMBLY_OPTIONS = ['Tháo rời (KD)', 'RÁP CHẾT (FA)']

export function ProductBomConfigTable({
  productCode,
  configs,
  availableColors = ['Đen piano', 'Ngà voi', 'Sồi tự nhiên', 'Óc chó (Walnut)'],
  availableWoods = ['Cao su', 'Tràm', 'Xoan đào'],
  onChange,
  onOpenBom,
}: ProductBomConfigTableProps) {
  // Popover state
  const [popover, setPopover] = useState<{
    configIdx: number
    field: 'colors' | 'woods' | 'assemblies'
    anchorRect: DOMRect | null
  } | null>(null)

  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopover(null)
      }
    }
    if (popover) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [popover])

  // Delete Confirmation Modal state
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState<number | null>(null)

  function handleAddRow() {
    const newRow = createBlankBomConfigRow(productCode)
    onChange([...configs, newRow])
  }

  function requestDeleteRow(index: number) {
    setDeleteConfirmIdx(index)
  }

  function confirmDeleteRow() {
    if (deleteConfirmIdx === null) return
    const updated = configs.filter((_, i) => i !== deleteConfirmIdx)
    onChange(updated)
    setDeleteConfirmIdx(null)
  }

  function handleStatusChange(index: number, newStatus: 'OK' | 'Đang Cập Nhật') {
    const updated = [...configs]
    updated[index] = { ...updated[index], status: newStatus }
    onChange(updated)
  }

  function handleToggleItem(item: string) {
    if (!popover) return
    const { configIdx, field } = popover
    const cfg = configs[configIdx]
    const currentList = cfg[field] || []
    const nextList = currentList.includes(item)
      ? currentList.filter((x) => x !== item)
      : [...currentList, item]

    const updated = [...configs]
    updated[configIdx] = { ...cfg, [field]: nextList }
    onChange(updated)
  }

  function handleSelectAll(items: string[]) {
    if (!popover) return
    const { configIdx, field } = popover
    const cfg = configs[configIdx]
    const updated = [...configs]
    updated[configIdx] = { ...cfg, [field]: [...items] }
    onChange(updated)
  }

  function handleDeselectAll() {
    if (!popover) return
    const { configIdx, field } = popover
    const cfg = configs[configIdx]
    const updated = [...configs]
    updated[configIdx] = { ...cfg, [field]: [] }
    onChange(updated)
  }

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)] p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded bg-emerald-100 text-emerald-700">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">
              BOM
            </h3>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddRow}
          className="inline-flex items-center gap-1.5 rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.98]"
        >
          <Plus className="h-3.5 w-3.5" />
          Tạo dòng cấu hình BOM
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-600">
              <th className="px-3 py-2.5" style={{ width: '130px' }}>
                MÃ HÀNG
              </th>
              <th className="px-3 py-2.5" style={{ minWidth: '220px' }}>
                MÀU ÁP DỤNG
              </th>
              <th className="px-3 py-2.5" style={{ minWidth: '200px' }}>
                GỖ ÁP DỤNG
              </th>
              <th className="px-3 py-2.5" style={{ minWidth: '200px' }}>
                KẾT CẤU
              </th>
              <th className="px-3 py-2.5 text-center" style={{ width: '150px' }}>
                TRẠNG THÁI
              </th>
              <th className="px-3 py-2.5 text-center" style={{ width: '160px' }}>
                THAO TÁC
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {configs.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-8 text-center text-xs text-slate-400"
                >
                  Chưa có cấu hình BOM nào cho mã hàng này. Nhấp vào "+ Tạo dòng cấu hình BOM" để bắt đầu thiết lập.
                </td>
              </tr>
            ) : (
              configs.map((cfg, idx) => {
              const colorText =
                cfg.colors && cfg.colors.length > 0 ? cfg.colors.join(' - ') : '— (Chưa chọn màu)'
              const woodText =
                cfg.woods && cfg.woods.length > 0 ? cfg.woods.join(' - ') : '— (Chưa chọn gỗ)'
              const asmText =
                cfg.assemblies && cfg.assemblies.length > 0
                  ? cfg.assemblies.join(' - ')
                  : '— (Chưa chọn kết cấu)'

              return (
                <tr key={cfg.id || idx} className="hover:bg-slate-50/70 transition-colors">
                  {/* Cột 1: Mã hàng - chỉ hiện mã sản phẩm (e.g. MP180) theo yêu cầu */}
                  <td className="px-3 py-2.5 font-mono text-xs font-bold text-slate-800">
                    <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-slate-900 border border-slate-200">
                      {productCode}
                    </span>
                  </td>

                  {/* Cột 2: Màu áp dụng */}
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={(e) =>
                        setPopover({
                          configIdx: idx,
                          field: 'colors',
                          anchorRect: e.currentTarget.getBoundingClientRect(),
                        })
                      }
                      className="group flex w-full items-center justify-between gap-1.5 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs text-rose-900 font-semibold hover:border-rose-300 hover:bg-rose-50/40"
                    >
                      <span className="truncate">{colorText}</span>
                      <ChevronDown className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-rose-600" />
                    </button>
                  </td>

                  {/* Cột 3: Gỗ áp dụng */}
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={(e) =>
                        setPopover({
                          configIdx: idx,
                          field: 'woods',
                          anchorRect: e.currentTarget.getBoundingClientRect(),
                        })
                      }
                      className="group flex w-full items-center justify-between gap-1.5 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs text-amber-900 font-semibold hover:border-amber-300 hover:bg-amber-50/40"
                    >
                      <span className="truncate">{woodText}</span>
                      <ChevronDown className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-amber-600" />
                    </button>
                  </td>

                  {/* Cột 4: Kết cấu áp dụng */}
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={(e) =>
                        setPopover({
                          configIdx: idx,
                          field: 'assemblies',
                          anchorRect: e.currentTarget.getBoundingClientRect(),
                        })
                      }
                      className="group flex w-full items-center justify-between gap-1.5 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs text-blue-900 font-semibold hover:border-blue-300 hover:bg-blue-50/40"
                    >
                      <span className="truncate">{asmText}</span>
                      <ChevronDown className="h-3 w-3 shrink-0 text-slate-400 group-hover:text-blue-600" />
                    </button>
                  </td>

                  {/* Cột 5: Trạng thái */}
                  <td className="px-3 py-2.5 text-center">
                    <select
                      value={cfg.status}
                      onChange={(e) =>
                        handleStatusChange(idx, e.target.value as 'OK' | 'Đang Cập Nhật')
                      }
                      className={`rounded px-2 py-1 text-xs font-bold border transition ${
                        cfg.status === 'OK'
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                          : 'border-amber-300 bg-amber-50 text-amber-800'
                      }`}
                    >
                      <option value="OK">● OK</option>
                      <option value="Đang Cập Nhật">⚡ Đang Cập Nhật</option>
                    </select>
                  </td>

                  {/* Cột 6: Thao tác */}
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onOpenBom(cfg)}
                        className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow-xs transition hover:bg-emerald-700 active:scale-95"
                        title="Mở toàn bộ bảng BOM bóc tách kỹ thuật"
                      >
                        <FolderOpen className="h-3.5 w-3.5" />
                        Mở bảng BOM
                      </button>
                      <button
                        type="button"
                        onClick={() => requestDeleteRow(idx)}
                        className="rounded p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                        title="Xóa cấu hình BOM này"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })
          )}
          </tbody>
        </table>
      </div>

      {/* Modal Xác nhận xóa BOM ("Khi xóa BOM thì phải hiện ra 1 bảng nữa để xác nhận") */}
      {deleteConfirmIdx !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in">
          <div
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl border border-slate-200 animate-in zoom-in-95 duration-150"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">
                  Xác nhận xóa BOM
                </h4>
                <p className="text-xs text-slate-500">
                  Bạn có chắc chắn muốn xóa dòng cấu hình BOM này không?
                </p>
              </div>
            </div>

            <div className="my-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Mã hàng:</span>
                <span className="font-mono font-bold text-slate-900 bg-slate-200 px-2 py-0.5 rounded">
                  {productCode}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Màu áp dụng:</span>
                <span className="font-semibold text-rose-800">
                  {configs[deleteConfirmIdx]?.colors?.join(' - ') || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Gỗ áp dụng:</span>
                <span className="font-semibold text-amber-800">
                  {configs[deleteConfirmIdx]?.woods?.join(' - ') || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Kết cấu:</span>
                <span className="font-semibold text-blue-800">
                  {configs[deleteConfirmIdx]?.assemblies?.join(' - ') || '—'}
                </span>
              </div>
              <p className="pt-2 text-[11px] text-red-600 font-medium border-t border-slate-200 mt-2">
                ⚠️ Thao tác này sẽ loại bỏ cấu hình BOM đã chọn khỏi bảng.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmIdx(null)}
                className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={confirmDeleteRow}
                className="rounded bg-red-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-red-700 transition cursor-pointer"
              >
                Xác nhận xóa BOM
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checklist Popover */}
      {popover && popover.anchorRect && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: `${popover.anchorRect.bottom + 4}px`,
            left: `${popover.anchorRect.left}px`,
            zIndex: 1000,
          }}
          className="w-64 rounded-md border border-slate-300 bg-white p-2.5 shadow-xl animate-in fade-in zoom-in-95 duration-100"
        >
          <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-1.5 text-xs font-bold uppercase text-slate-600">
            <span>
              {popover.field === 'colors'
                ? 'Chọn Màu Áp Dụng'
                : popover.field === 'woods'
                  ? 'Chọn Gỗ Áp Dụng'
                  : 'Chọn Kết Cấu'}
            </span>
            <button
              type="button"
              onClick={() => setPopover(null)}
              className="text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>

          <div className="max-h-48 space-y-1 overflow-y-auto">
            {(() => {
              const options =
                popover.field === 'colors'
                  ? availableColors
                  : popover.field === 'woods'
                    ? availableWoods
                    : DEFAULT_ASSEMBLY_OPTIONS
              const currentSelected = configs[popover.configIdx]?.[popover.field] || []

              if (options.length === 0) {
                return (
                  <div className="p-3 text-center text-xs text-slate-400 italic">
                    Chưa có tùy chọn nào được cấu hình ở thông tin sản phẩm
                  </div>
                )
              }

              return options.map((opt) => {
                const isChecked = currentSelected.includes(opt)
                return (
                  <label
                    key={opt}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-50 transition"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggleItem(opt)}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="font-medium text-slate-800">{opt}</span>
                  </label>
                )
              })
            })()}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => {
                const options =
                  popover.field === 'colors'
                    ? availableColors
                    : popover.field === 'woods'
                      ? availableWoods
                      : DEFAULT_ASSEMBLY_OPTIONS
                handleSelectAll(options)
              }}
              className="text-[11px] font-semibold text-emerald-700 hover:underline"
            >
              Chọn tất cả
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-[11px] font-semibold text-slate-500 hover:underline"
            >
              Bỏ chọn
            </button>
            <button
              type="button"
              onClick={() => setPopover(null)}
              className="rounded bg-slate-800 px-2.5 py-0.5 text-[11px] font-semibold text-white hover:bg-slate-900"
            >
              Xong
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
